import Add from '@mui/icons-material/Add'
import Close from '@mui/icons-material/Close'
import { CircularProgress, DialogContent, Fade, IconButton } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CongratulatoryTimerHeader } from 'src/consumer/components/TimerComponent/CongratulatoryFlow/styledComponents'
import {
  PracticeTimerCongratulatoryDialogComponent,
  PracticeTimerCongratulatoryDialogPaper,
} from 'src/consumer/components/TimerComponent/TimerComponent/styledComponents'
import { PRACTICE_TIMER_TYPES } from 'src/consumer/constants'
import { useUnsplashImages } from 'src/consumer/hooks/useUnsplashImages'
import {
  getMyTimersOnlyFromServer,
  getPopularAndMyTimersFromServer,
} from 'src/consumer/services/featureServices'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { MyTimersListingHeaderContainer } from '../styledComponents'
import TimerAddEditDialog from './AddEditDialog/AddEditDialog'
import TimerGridListingPage from './Grid/TimerGridListingPage'
import {
  AddTimerButton,
  MyTimersItemsListingContainer,
  TimerListingTab,
  TimerListingTabsContainer,
  TimersListingLoadingOverlay,
} from './styledComponents'
import { MY_TIMERS_TYPES } from './utils'

const imagesToGet = 10

export default function TimersListingController() {
  // Fetch Unsplash images on-demand when this component mounts
  const { images: unsplashImages } = useUnsplashImages({
    query: 'nature',
    perPage: 10,
    orientation: 'landscape',
    enabled: true,
    defer: true, // Defer until browser is idle for better performance
  })

  const [serverItems, setServerItems] = useState({
    myTimers: [],
    popularTimers: [],
  })

  const [timerToEdit, setTimerToEdit] = useState()
  const imageUrls = unsplashImages?.results

  const [loading, setLoading] = useState()

  const [listingType, setListingType] = useState('grid')
  const [timersType, setTimersType] = useState(MY_TIMERS_TYPES.MY_TIMERS)
  const [showTimerEditAddDialog, setShowTimerEditAddDialog] = useState(false)

  const items = useMemo(() => {
    switch (timersType) {
      case MY_TIMERS_TYPES.MY_TIMERS_WITH_POPULAR_MEDITATIONS:
        return serverItems?.popularTimers
      case MY_TIMERS_TYPES.MY_TIMERS:
        return serverItems?.myTimers
      default:
        return serverItems?.myTimers
    }
  }, [serverItems, timersType])

  const navigate = useNavigate()

  async function getAndSetMyTimersOnlyList() {
    const timers = await getMyTimersOnlyFromServer()
    if (timers) {
      setServerItems((prev) => ({ ...prev, myTimers: timers }))
    }
  }
  async function getAndSetMyTimersWithPopularMeditationsList() {
    const timers = await getPopularAndMyTimersFromServer()
    if (timers) {
      setServerItems((prev) => ({ ...prev, popularTimers: timers }))
    }
  }

  async function MyTimersSetup() {
    await getAndSetMyTimersOnlyList()
  }

  async function MyTimersWithPopularMeditationsSetup() {
    await getAndSetMyTimersWithPopularMeditationsList()
  }

  async function refreshFunction() {
    setLoading(true)
    switch (timersType) {
      case MY_TIMERS_TYPES.MY_TIMERS:
        await MyTimersSetup()
        break
      case MY_TIMERS_TYPES.MY_TIMERS_WITH_POPULAR_MEDITATIONS:
        await MyTimersWithPopularMeditationsSetup()
        break
      default:
        break
    }
    setLoading(false)
    closeTimerEditAddDialog()
  }

  async function Setup() {
    setLoading(true)
    const promises = [MyTimersSetup(), MyTimersWithPopularMeditationsSetup()]
    await Promise.all(promises)
    setLoading(false)
  }

  function changeTimerType(value) {
    switch (value) {
      case MY_TIMERS_TYPES.MY_TIMERS:
        MyTimersSetup()
        break
      case MY_TIMERS_TYPES.MY_TIMERS_WITH_POPULAR_MEDITATIONS:
        MyTimersWithPopularMeditationsSetup()
        break
      default:
        break
    }
    setTimersType(value)
  }

  function openTimerEditAddDialog(timerToEdit) {
    setTimerToEdit(timerToEdit)
    setShowTimerEditAddDialog(true)
  }

  function closeTimerEditAddDialog() {
    setShowTimerEditAddDialog(false)
  }

  useEffect(() => {
    Setup()
  }, [])

  function actionButtonLogic() {
    return {
      text: 'Start',
      onClick: (item) => {
        const itemToUse = timersType === MY_TIMERS_TYPES.MY_TIMERS ? item.meditation : item
        itemToUse.slug
          ? navigate(`/timer/${itemToUse.slug}`, {
              state: {
                item: itemToUse,
              },
            })
          : navigate(`/timer/i/${itemToUse.id}`, {
              state: {
                item: itemToUse,
              },
            })
      },
    }
  }

  function getTitleFieldLogic(item) {
    const itemToUse = timersType === MY_TIMERS_TYPES.MY_TIMERS ? item.meditation : item

    return itemToUse?.title
  }

  function getMainImage(item, imgUrls, idx) {
    const itemToUse = timersType === MY_TIMERS_TYPES.MY_TIMERS ? item.meditation : item

    if (itemToUse?.coverIcon?.includes('MEDIA')) {
      return getImageLink(itemToUse?.coverIcon)
    }

    const randomNumber = idx % imagesToGet

    var unsplashImageUrlToUse = imgUrls?.[randomNumber]

    return unsplashImageUrlToUse?.urls?.regular || getImageLink(undefined, undefined)
  }

  function getSecondaryText(item) {
    const itemToUse = timersType === MY_TIMERS_TYPES.MY_TIMERS ? item.meditation : item
    return itemToUse?.description
  }

  function getSubtitleField(item) {
    const itemToUse = timersType === MY_TIMERS_TYPES.MY_TIMERS ? item.meditation : item
    var durationString = `${Math.trunc(itemToUse?.duration / 1000 / 60)} minutes`

    switch (itemToUse?.durationType) {
      case PRACTICE_TIMER_TYPES.unlimited:
        durationString = '∞'
        break
      case PRACTICE_TIMER_TYPES.range:
        durationString = `${Math.trunc(itemToUse?.duration / 1000 / 60)} - ${
          itemToUse?.maxDuration / 1000 / 60
        } minutes`
        break
      default:
    }

    return `Duration: ${durationString}`
  }

  const ListingComponent = useMemo(() => {
    switch (listingType) {
      case 'list':
      // return TimerVerticalListingPage
      case 'grid':
        return TimerGridListingPage
      default:
        return TimerGridListingPage
    }
  }, [listingType])

  return (
    (serverItems?.myTimers?.length > 0 || serverItems?.popularTimers?.length > 0) && (
      <>
        <MyTimersListingHeaderContainer>
          <h5 className="my-timer-heading">My Practice Timers</h5>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <AddTimerButton onClick={() => openTimerEditAddDialog()}>
              <span className="btn-txt">Create Custom Timer</span>
              <Add />
            </AddTimerButton>
          </div>
        </MyTimersListingHeaderContainer>
        <TimerListingTabsContainer value={timersType} onChange={(e, val) => changeTimerType(val)}>
          <TimerListingTab label="My Timers" value={MY_TIMERS_TYPES.MY_TIMERS} disabled={loading} />
          <TimerListingTab
            label="Popular Timers"
            value={MY_TIMERS_TYPES.MY_TIMERS_WITH_POPULAR_MEDITATIONS}
            disabled={loading}
          />
        </TimerListingTabsContainer>
        <MyTimersItemsListingContainer>
          <Fade in={loading}>
            <TimersListingLoadingOverlay>
              <CircularProgress sx={{ color: '#fff' }} />
            </TimersListingLoadingOverlay>
          </Fade>
          <ListingComponent
            items={items}
            getTitleField={getTitleFieldLogic}
            getActionButtonLogic={actionButtonLogic}
            getMainImage={(itm, idx) => getMainImage(itm, imageUrls, idx)}
            getSecondaryText={getSecondaryText}
            openTimerEditAddDialog={openTimerEditAddDialog}
            getSubtitleField={getSubtitleField}
          />
        </MyTimersItemsListingContainer>
        <PracticeTimerCongratulatoryDialogComponent
          open={showTimerEditAddDialog}
          PaperComponent={PracticeTimerCongratulatoryDialogPaper}
          PaperProps={{
            longDialog: true,
          }}
        >
          <CongratulatoryTimerHeader>
            <span>Create a timer</span>
            <IconButton sx={{ color: '#fff' }} onClick={closeTimerEditAddDialog}>
              <Close />
            </IconButton>
          </CongratulatoryTimerHeader>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <TimerAddEditDialog refreshFunction={refreshFunction} timerToEdit={timerToEdit} />
          </DialogContent>
        </PracticeTimerCongratulatoryDialogComponent>
      </>
    )
  )
}

/* <TimerListingTypeToggleSwitchContainer
            value={listingType}
            exclusive
            onChange={(e, value) => setListingType(value)}
          >
            <TimerListingTypeToggleSwitchButton
              selected={listingType === 'grid'}
              onClick={() => setListingType('grid')}
            >
              <GridView />
            </TimerListingTypeToggleSwitchButton>
            <TimerListingTypeToggleSwitchButton
              selected={listingType === 'list'}
              onClick={() => setListingType('list')}
            >
              <ViewList />
            </TimerListingTypeToggleSwitchButton>
          </TimerListingTypeToggleSwitchContainer> */
